import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, label, TONE, notifyErr, fmtDate, fmtDateTime, fmtMoney } from "@/lib/api";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CUSTOMER_FIELDS, withCargos, withRequired } from "@/lib/forms";
import AiCustomerFill, { uploadCustomerFiles } from "@/components/AiCustomerFill";

const TONES = {
  green: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", blue: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  amber: "bg-orange-500/15 text-orange-300 border-orange-500/30", red: "bg-red-500/15 text-red-300 border-red-500/30",
  zinc: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30", violet: "bg-violet-500/15 text-violet-300 border-violet-500/30",
};

export const StatusBadge = ({ value, text, tone, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[11px] font-medium whitespace-nowrap ${TONES[tone || TONE[value] || "zinc"]}`}>
    {text || label(value)}
  </span>
);

export const PageHeader = ({ title, subtitle, children }) => (
  <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6 fade-up">
    <div>
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-white" data-testid="page-title">{title}</h1>
      {subtitle && <p className="text-sm text-zinc-400 mt-1">{subtitle}</p>}
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export const Panel = ({ title, action, children, className = "", testid }) => (
  <section className={`bg-card border border-zinc-800 rounded-xl ${className}`} data-testid={testid}>
    {title && <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-800"><h3 className="font-display font-semibold panel-title">{title}</h3>{action}</div>}
    <div className="p-5">{children}</div>
  </section>
);

export function ConfirmDialog({ open, onOpenChange, title, description, confirmText = "Conferma", cancelText = "Annulla", onConfirm, destructive }) {
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="confirm-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle>{description && <DialogDescription>{description}</DialogDescription>}</DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="confirm-cancel">{cancelText}</Button>
          <Button variant={destructive ? "destructive" : "default"} disabled={busy} data-testid="confirm-ok"
            onClick={async () => { setBusy(true); try { await onConfirm(); onOpenChange(false); } finally { setBusy(false); } }}>
            {busy && <Loader2 className="animate-spin mr-1" size={14} />}{confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function useList(path, params) {
  const [data, setData] = useState(null);
  const key = JSON.stringify(params || {});
  const reload = () => api.get(path, { params }).then((r) => setData(r.data)).catch((e) => { notifyErr(e); setData([]); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [path, key]);
  return [data, reload];
}

const REF = {
  vehicle: ["/vehicles", (v) => `${v.plate} · ${v.brand || ""} ${v.model || ""}`],
  customer: ["/customers", (c) => `${c.last_name || ""} ${c.first_name || ""}`.trim() + (c.tax_code ? ` · ${c.tax_code}` : "")],
  employee: ["/employees", (e) => `${e.first_name} ${e.last_name}`, { active: "true" }],
  booking: ["/bookings", (b) => `${b.code} · ${b.customer_name || ""}`],
  rental: ["/rentals", (r) => `${r.code} · ${r.customer_name || ""}`],
};

export function RefSelect({ kind, value, onChange, testid, allowEmpty = true }) {
  const [opts, setOpts] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    const [path, , extra] = REF[kind];
    api.get(path, { params: { ...(extra || {}), limit: 500 } }).then((r) => setOpts(r.data)).catch(() => {});
  }, [kind]);
  const fmt = REF[kind][1];
  const filtered = opts.filter((o) => !q || fmt(o).toLowerCase().includes(q.toLowerCase())).slice(0, 80);
  return (
    <Select value={value || "__none"} onValueChange={(v) => onChange(v === "__none" ? null : v)}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder="Seleziona…" /></SelectTrigger>
      <SelectContent>
        {opts.length > 8 && <div className="p-1"><Input placeholder="Filtra…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.stopPropagation()} className="h-8" /></div>}
        {allowEmpty && <SelectItem value="__none">— Nessuno —</SelectItem>}
        {filtered.map((o) => <SelectItem key={o.id} value={o.id}>{fmt(o)}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function QuickCustomer({ value, onChange, testid }) {
  const { state, can } = useAuth();
  const [open, setOpen] = useState(false);
  const [k, setK] = useState(0);
  const [ai, setAi] = useState(null);
  const ai_on = state.features?.ai;
  return (
    <div className="flex gap-2">
      <div className="flex-1 min-w-0"><RefSelect key={k} kind="customer" value={value} onChange={onChange} testid={testid} /></div>
      {can("customers", "create") && <Button type="button" variant="outline" onClick={() => { setAi(null); setOpen(true); }} title="Nuovo cliente" data-testid={`${testid}-new`}><Plus size={15} className="mr-1" />Nuovo cliente</Button>}
      <FormDialog open={open} onOpenChange={setOpen} title="Nuovo cliente" fields={withRequired(withCargos(CUSTOMER_FIELDS, state.features?.cargos), state.company, "customers")}
        top={ai_on ? (form, setForm) => <div className="flex flex-wrap items-center gap-3 rounded-lg border border-zinc-800 p-3" data-testid="quick-customer-ai">
          <AiCustomerFill label="Compila con AI dai documenti" testid="quick-customer-ai-btn" onApply={(fields, files, docs) => { setForm((s) => ({ ...s, ...fields })); setAi({ files, docs }); }} />
          <span className="text-xs text-zinc-500">{ai ? "Dati letti con AI: controllali prima di salvare. I documenti verranno allegati al cliente." : "Carica carta d'identità, patente o codice fiscale per compilare i campi in automatico."}</span></div> : undefined}
        onSubmit={async (f) => { const r = await api.post("/customers", f); if (ai) await uploadCustomerFiles(r.data.id, ai.files, ai.docs); toast.success(ai ? "Cliente creato con i documenti allegati e selezionato" : "Cliente creato e selezionato"); setK((x) => x + 1); onChange(r.data.id); }} />
    </div>
  );
}

export function Field({ f, value, onChange }) {
  const id = `field-${f.name}`;
  let input;
  if (f.quickCreate) {
    input = <QuickCustomer value={value} onChange={onChange} testid={id} />;
  } else if (f.type === "select") {
    input = (
      <Select value={value ?? ""} onValueChange={onChange}>
        <SelectTrigger data-testid={id}><SelectValue placeholder="Seleziona…" /></SelectTrigger>
        <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{label(o)}</SelectItem>)}</SelectContent>
      </Select>
    );
  } else if (REF[f.type]) {
    input = <RefSelect kind={f.type} value={value} onChange={onChange} testid={id} />;
  } else if (f.type === "textarea") {
    input = <Textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={id} rows={3} />;
  } else if (f.type === "switch") {
    input = <div className="h-10 flex items-center"><Switch checked={!!value} onCheckedChange={onChange} data-testid={id} /></div>;
  } else {
    input = <Input type={f.type === "datetime" ? "datetime-local" : f.type || "text"} value={value ?? ""} data-testid={id} step={f.type === "number" ? "any" : undefined}
      onChange={(e) => onChange(f.type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)} />;
  }
  return (
    <div className={f.full ? "sm:col-span-2" : ""}>
      <Label className="text-xs text-zinc-400 mb-1.5 block">{f.label}{f.required && <span className="text-red-400"> *</span>}</Label>
      {input}
      {(f.type === "date" || f.type === "datetime") && value && <div className="text-[11px] text-zinc-500 mt-1" data-testid={`${id}-it`}>{f.type === "date" ? fmtDate(value) : fmtDateTime(value)}</div>}
    </div>
  );
}

export function FormDialog({ open, onOpenChange, title, fields, initial, onSubmit, extra, top, steps }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(0);
  useEffect(() => { if (open) { setForm(initial || {}); setStep(0); } }, [open, initial]);
  const last = !steps || step === steps.length - 1;
  const shown = steps ? fields.filter((f) => steps[step].names.includes(f.name)) : fields;
  const missing = (list) => list.filter((f) => f.required && (form[f.name] === undefined || form[f.name] === null || form[f.name] === ""));
  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const miss = missing(last ? fields : shown);
    if (miss.length) { notifyErr({ message: `Compila i campi obbligatori: ${miss.map((m) => m.label).join(", ")}` }); return; }
    if (!last) { setStep(step + 1); return; }
    setBusy(true);
    try { await onSubmit(form); onOpenChange(false); } catch (err) { notifyErr(err); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto scroll-thin" data-testid="form-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        {steps && <div className="flex flex-wrap gap-2" data-testid="form-steps">{steps.map((s, i) => (
          <button type="button" key={s.title} onClick={() => i < step && setStep(i)} data-testid={`form-step-${i + 1}`}
            className={`text-xs px-3 py-1.5 rounded-full border ${i === step ? "border-brand bg-brand/15 text-white" : i < step ? "border-zinc-700 text-zinc-300" : "border-zinc-800 text-zinc-500"}`}>{i + 1}. {s.title}</button>))}</div>}
        <form onSubmit={submit} className="space-y-5">
          {top && top(form, setForm, step)}
          {shown.length > 0 && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {shown.filter((f) => !f.hidden).map((f) => <Field key={f.name} f={f} value={form[f.name]} onChange={(v) => setForm((s) => ({ ...s, [f.name]: v }))} />)}
          </div>}
          {extra && extra(form, setForm, step)}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="form-cancel">Annulla</Button>
            {steps && step > 0 && <Button type="button" variant="outline" onClick={() => setStep(step - 1)} data-testid="form-prev">Indietro</Button>}
            <Button type="submit" disabled={busy} data-testid={last ? "form-submit" : "form-next"}>{busy && <Loader2 className="animate-spin mr-1" size={14} />}{last ? "Salva" : "Avanti"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function renderCell(col, row) {
  const v = row[col.key];
  if (col.render) return col.render(row);
  switch (col.type) {
    case "status": return <StatusBadge value={v} />;
    case "date": return fmtDate(v);
    case "datetime": return fmtDateTime(v);
    case "money": return fmtMoney(v);
    case "mono": return <span className="font-mono text-xs">{v || "—"}</span>;
    case "bool": return v ? "Sì" : "No";
    default: return v === null || v === undefined || v === "" ? "—" : String(v);
  }
}

export function DataTable({ columns, rows, onRowClick, testid = "data-table", empty = "Nessun elemento trovato", actions }) {
  if (rows === null) return <div className="py-16 grid place-items-center text-zinc-500"><Loader2 className="animate-spin" /></div>;
  return (
    <div className="bg-card border border-zinc-800 rounded-xl overflow-x-auto scroll-thin" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="bg-black text-zinc-400 text-[11px] uppercase tracking-wider">
          <tr>{columns.map((c) => <th key={c.key} className="text-left font-medium px-4 py-3 whitespace-nowrap">{c.label}</th>)}{actions && <th className="px-4 py-3" />}</tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={columns.length + 1} className="text-center text-zinc-500 py-12" data-testid={`${testid}-empty`}>{empty}</td></tr>}
          {rows.map((r) => (
            <tr key={r.id} onClick={() => onRowClick && onRowClick(r)} data-testid={`${testid}-row-${r.id}`}
              className={`tbl-row border-t border-zinc-800/70 transition-colors ${onRowClick ? "cursor-pointer hover:bg-zinc-800/40" : ""}`}>
              {columns.map((c) => <td key={c.key} className="px-4 py-3 whitespace-nowrap">{renderCell(c, r)}</td>)}
              {actions && <td className="px-4 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>{actions(r)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export const InfoGrid = ({ items }) => (
  <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
    {items.map(([k, v], i) => (
      <div key={i}><dt className="text-[11px] uppercase tracking-wider text-zinc-500">{k}</dt><dd className="text-sm text-zinc-100 mt-0.5 break-words">{v ?? "—"}</dd></div>
    ))}
  </dl>
);

export const FilterSelect = ({ value, onChange, options, placeholder, testid }) => (
  <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
    <SelectTrigger className="w-full sm:w-44 h-10" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{placeholder}: tutti</SelectItem>
      {options.map((o) => (Array.isArray(o) ? <SelectItem key={o[0]} value={o[0]}>{o[1]}</SelectItem> : <SelectItem key={o} value={o}>{label(o)}</SelectItem>))}
    </SelectContent>
  </Select>
);
